import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';
import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { trackListingEvent, trackUserEvent } from '../../analytics';
import { SEMANTIC_EVENT_NAME, TOPIC_TAG_TXT, TRIGGER_TYPE, type TriggerLocation } from './globals';
import type { ZrmClickstreamUserOrNull } from '../../analytics/types';

const SAVED_REPLY_MODAL = 'saved_reply_modal';

// https://dataportal.zillowgroup.net/event-registry/3373
export const CHOOSE_SAVED_REPLY_CLICK_EVENT_TYPE_ID = '3373';
export const emitChooseSavedReplyClickEvent = (user: ZrmClickstreamUserOrNull, triggerLocation: TriggerLocation) =>
  trackUserEvent({
    envelope: {
      event_type_id: CHOOSE_SAVED_REPLY_CLICK_EVENT_TYPE_ID,
      event_type_version_id: '2',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'saved_reply_button',
      trigger_object_nm: 'message_window',
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.ReplyMessage,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2543
export const CHOOSE_CUSTOM_REPLY_CLICK_EVENT_TYPE_ID = '2543';
export const emitChooseCustomReplyClickEvent = (
  user: ZrmClickstreamUserOrNull,
  listing: ZrmClickstreamListing,
  triggerLocation: TriggerLocation,
) =>
  trackListingEvent({
    envelope: {
      event_type_id: CHOOSE_CUSTOM_REPLY_CLICK_EVENT_TYPE_ID,
      event_type_version_id: '2',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'custom_reply',
      trigger_object_nm: SAVED_REPLY_MODAL,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.ReplyMessage,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/2544
export const emitChooseDefaultReplyClickEvent = (
  user: ZrmClickstreamUserOrNull,
  listing: ZrmClickstreamListing,
  triggerLocation: TriggerLocation,
) =>
  trackListingEvent({
    envelope: {
      event_type_id: '2544',
      event_type_version_id: '2',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'default_reply',
      trigger_object_nm: SAVED_REPLY_MODAL,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.ManageSavedReply,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/2547
export const emitCustomReplyCreatedEvent = (
  user: ZrmClickstreamUserOrNull,
  listing: ZrmClickstreamListing,
  triggerLocation: TriggerLocation,
) =>
  trackListingEvent({
    envelope: {
      event_type_id: '2547',
      event_type_version_id: '2',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'custom_reply_save',
      trigger_object_nm: SAVED_REPLY_MODAL,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.ManageSavedReply,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/2545
export const emitCustomReplyEditedEvent = (
  user: ZrmClickstreamUserOrNull,
  listing: ZrmClickstreamListing,
  triggerLocation: TriggerLocation,
) =>
  trackListingEvent({
    envelope: {
      event_type_id: '2545',
      event_type_version_id: '2',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'custom_reply_edit',
      trigger_object_nm: SAVED_REPLY_MODAL,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.ManageSavedReply,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/2546
export const emitDefaultReplyEditedEvent = (
  user: ZrmClickstreamUserOrNull,
  listing: ZrmClickstreamListing,
  triggerLocation: TriggerLocation,
) =>
  trackListingEvent({
    envelope: {
      event_type_id: '2546',
      event_type_version_id: '2',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'default_reply_edit_button',
      trigger_object_nm: SAVED_REPLY_MODAL,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.ManageSavedReply,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });
